import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { mapboxBasemapPreviewUrl, mapboxStylePathFromUrl } from './webMapMapboxUtils';

export interface BasemapOption {
  key: string;
  label: string;
  styleUrl: string;
  /** Fallback when no token or image error */
  swatch: string;
}

interface MapboxBasemapStripProps {
  accessToken: string;
  value: string;
  options: BasemapOption[];
  onChange: (key: string) => void;
}

const MapboxBasemapStrip: React.FC<MapboxBasemapStripProps> = ({
  accessToken,
  value,
  options,
  onChange,
}) => {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
      {options.map((opt) => {
        const active = value === opt.key;
        const preview =
          accessToken
            ? mapboxBasemapPreviewUrl(mapboxStylePathFromUrl(opt.styleUrl), accessToken)
            : null;
        return (
          <Tooltip key={opt.key} title={opt.label}>
            <Box
              component="button"
              type="button"
              onClick={() => onChange(opt.key)}
              sx={{
                border: 2,
                borderColor: active ? 'primary.main' : 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                cursor: 'pointer',
                p: 0,
                bgcolor: 'background.paper',
                display: 'block',
                minWidth: 72,
                transition: 'box-shadow 0.15s',
                boxShadow: active ? 2 : 0,
                '&:hover': { borderColor: 'primary.light' },
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 48,
                  bgcolor: opt.swatch,
                  backgroundImage: preview ? `url("${preview}")` : undefined,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  py: 0.25,
                  px: 0.5,
                  fontWeight: active ? 700 : 400,
                  color: active ? 'primary.main' : 'text.secondary',
                  lineHeight: 1.1,
                }}
              >
                {opt.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export default MapboxBasemapStrip;
